/**
 * useDraggablePosition — reusable hook that turns any element into a
 * draggable floating widget with offset persistence.
 *
 * Storage strategy:
 *  - localStorage `rp_widget_pos_v1` — fast cache, works anonymously
 *  - backend `/me/preferences.widget_positions` — server source of truth for
 *    logged-in users (so position roams across devices)
 *
 * Returns:
 *  - offset: {x, y} translation applied to the anchor (default 0,0)
 *  - dragProps: spread onto the drag-handle element (PointerEvent based,
 *    no jQuery/no libs; works on touch + mouse + pen)
 *  - reset(): clear the offset and persist that (so widget snaps back to
 *    default anchor like bottom-4 right-4)
 *  - dragging: bool — useful for disabling click during drag
 *  - moved: bool — true when offset != (0,0); show the "reset" chip only then
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { userPrefs as prefsApi } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

import { log } from "@/lib/log";
const LS_KEY = "rp_widget_pos_v1";
// Threshold (px) — pointer movement smaller than this counts as a click,
// larger movement counts as a drag.
const DRAG_THRESHOLD = 5;

function readAll() {
  if (typeof window === "undefined") return {};
  try {
    const raw = window.localStorage?.getItem(LS_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
}

function writeAll(map) {
  try {
    window.localStorage?.setItem(LS_KEY, JSON.stringify(map));
  } catch {
    /* quota or private-mode — ignore */
  }
}

export function useDraggablePosition(widgetKey, { defaultOffset = { x: 0, y: 0 } } = {}) {
  const { user } = useAuth();
  const [offset, setOffset] = useState(() => {
    const all = readAll();
    return all[widgetKey] || defaultOffset;
  });
  const [dragging, setDragging] = useState(false);
  const startRef = useRef({ pointerX: 0, pointerY: 0, baseX: 0, baseY: 0, dragged: false });

  // Hydrate from backend for logged-in users — server is source of truth.
  useEffect(() => {
    if (!user) return;
    prefsApi.get()
      .then((p) => {
        const remote = p?.widget_positions?.[widgetKey];
        if (remote && Number.isFinite(remote.x) && Number.isFinite(remote.y)) {
          setOffset({ x: remote.x, y: remote.y });
          const all = readAll();
          all[widgetKey] = remote;
          writeAll(all);
        }
      })
      .catch(() => { /* anonymous / offline — ignore */ });
  }, [user, widgetKey]);

  const persist = useCallback((next) => {
    const all = readAll();
    all[widgetKey] = next;
    writeAll(all);
    if (user) {
      prefsApi.update({ widget_positions: { [widgetKey]: next } }).catch((err) => {
        log.warn("[useDraggablePosition] backend sync failed:", err?.message);
      });
    }
  }, [widgetKey, user]);

  const onPointerDown = useCallback((e) => {
    if (!e.isPrimary) return;
    // Don't hijack clicks on the dismiss/close/control sub-buttons inside
    // the drag handle — those have `data-no-drag` on them.
    if (e.target?.closest?.("[data-no-drag]")) return;
    startRef.current = {
      pointerX: e.clientX,
      pointerY: e.clientY,
      baseX: offset.x,
      baseY: offset.y,
      dragged: false,
    };
    setDragging(true);
    // Capture so we keep getting events even if the pointer leaves the element.
    try { e.currentTarget?.setPointerCapture?.(e.pointerId); } catch { /* ignore */ }
  }, [offset.x, offset.y]);

  const onPointerMove = useCallback((e) => {
    if (!dragging) return;
    const dx = e.clientX - startRef.current.pointerX;
    const dy = e.clientY - startRef.current.pointerY;
    if (!startRef.current.dragged && Math.hypot(dx, dy) > DRAG_THRESHOLD) {
      startRef.current.dragged = true;
    }
    if (startRef.current.dragged) {
      // Note: offset.y is the offset FROM the anchor. Since the anchor is at
      // bottom-right (or bottom-left), positive Y in screen coords means
      // moving DOWN, which means moving CLOSER to the bottom anchor — so we
      // negate.
      setOffset({
        x: startRef.current.baseX - dx,
        y: startRef.current.baseY - dy,
      });
    }
  }, [dragging]);

  const onPointerUp = useCallback((e) => {
    if (!dragging) return;
    setDragging(false);
    if (startRef.current.dragged) {
      // Persist the final position. Suppress the click event that follows.
      const final = { x: offset.x, y: offset.y };
      persist(final);
      // Suppress the click that will fire immediately after pointerup —
      // user dragged, didn't click.
      const target = e.currentTarget;
      const suppressClick = (ev) => {
        ev.preventDefault();
        ev.stopPropagation();
        target?.removeEventListener("click", suppressClick, true);
      };
      target?.addEventListener("click", suppressClick, true);
      // Safety: remove the listener after 50ms even if no click fires.
      setTimeout(() => target?.removeEventListener("click", suppressClick, true), 50);
    }
  }, [dragging, offset.x, offset.y, persist]);

  const reset = useCallback(() => {
    setOffset(defaultOffset);
    persist(defaultOffset);
  }, [defaultOffset, persist]);

  const dragProps = {
    onPointerDown,
    onPointerMove,
    onPointerUp,
    onPointerCancel: onPointerUp,
    style: {
      touchAction: "none", // prevent scrolling while dragging on touch devices
      cursor: dragging ? "grabbing" : "grab",
    },
  };

  const moved = offset.x !== defaultOffset.x || offset.y !== defaultOffset.y;

  return { offset, dragProps, reset, dragging, moved };
}
